import { useApolloClient } from '@apollo/client';
import { SearchPageType } from '@zoopla/listing-search-context';
import { GEO_LOCATIONS_QUERY } from '@zoopla/listing-search-queries';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { FC, KeyboardEvent, useMemo } from 'react';
import { InputAutoSuggestWithLabel } from './InputAutoSuggestWithLabel/InputAutoSuggestWithLabel';
import { transformGeoLocationData } from './transforms';

type Tab = SearchPageType | 'house-prices';

type LocationSearchInputProps = {
    id?: string;
    onLocationInputKeyDown: () => void;
    onInputTextChange?: (value: string) => void;
    hasExtendedHeight?: boolean;
    hasBorderRight?: boolean;
    hasDarkPurpleBorder?: boolean;
    onlyDisplayRemoveIconWhenActive?: boolean;
    searchType?: Tab;
    value: string;
    showError?: boolean;
    onLocationSelect?: ({
        identifier,
        q
    }: {
        identifier: string;
        q: string;
    }) => void;
    handleInputTextChange?: (value: string) => void;
    onClearText?: () => void;
};

const forSaleToRentPlaceholder = 'eg. Oxford or NW3';
const locationRegex = /([A-Za-zÀ-ÖØ-öø-ÿ])/;

export const LocationSearchInput: FC<
    React.PropsWithChildren<LocationSearchInputProps>
> = ({
    id,
    onLocationInputKeyDown,
    hasExtendedHeight,
    hasBorderRight,
    hasDarkPurpleBorder,
    onlyDisplayRemoveIconWhenActive,
    value,
    showError = false,
    onLocationSelect,
    handleInputTextChange,
    onClearText
}) => {
    const client = useApolloClient();

    const handleInputKeyDown = (evt: KeyboardEvent) => {
        evt.preventDefault();

        if (evt.key === 'Enter') {
            onLocationInputKeyDown();
        }
    };

    const geoQuery = (value: string) => {
        return client.query({
            query: GEO_LOCATIONS_QUERY,
            variables: {
                locationPrefix: value.replace(/[^a-zA-Z0-9 ]/gi, '')
            }
        });
    };

    const geoQueryDebounced = AwesomeDebouncePromise(geoQuery, 300, {
        onlyResolvesLast: true
    });

    const getGeoLocationData = useMemo(
        () => async (value: string) => {
            const { data } = await geoQueryDebounced(value);
            return transformGeoLocationData(data.geoSuggestion);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <InputAutoSuggestWithLabel
            getData={getGeoLocationData}
            suggestionSelectedAction={({ id, value }) => {
                onLocationSelect &&
                    onLocationSelect({
                        identifier: id,
                        q: value as string
                    });
            }}
            placeholder={forSaleToRentPlaceholder}
            validInputRegex={locationRegex}
            showError={showError}
            id={id}
            value={value}
            autoComplete="off"
            inputOnKeyDown={handleInputKeyDown}
            inputTextChange={handleInputTextChange}
            hasExtendedHeight={hasExtendedHeight}
            hasDarkPurpleBorder={hasDarkPurpleBorder}
            hasBorderRight={hasBorderRight}
            onlyDisplayRemoveIconWhenActive={onlyDisplayRemoveIconWhenActive}
            onClearText={onClearText}
        />
    );
};
